/**
 * Filter dropdown that always renders with a light background (avoids OS/browser
 * native select dark theme). Use for table filter dropdowns where native <select>
 * can show a dark gray list.
 *
 * When `portalList` is true, the option list is rendered in `document.body` with
 * `position: fixed` so it is not clipped by scroll/overflow ancestors (e.g. client layout content).
 */
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './LightFilterSelect.css';

/**
 * @param {{ options: Array<{ value: string, label: string }>, value: string, onChange: (value: string) => void, 'aria-label'?: string, className?: string, portalList?: boolean }} props
 */
export default function LightFilterSelect({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className = '',
  portalList = false,
}) {
  const [open, setOpen] = useState(false);
  const [listBox, setListBox] = useState(() => ({ top: 0, left: 0, width: 0, maxHeight: 260 }));
  const ref = useRef(null);
  const listPortalRef = useRef(null);

  const syncListPosition = useCallback(() => {
    if (!portalList || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const margin = 6;
    const maxH = Math.max(120, Math.min(260, window.innerHeight - r.bottom - margin));
    setListBox({
      top: r.bottom + 2,
      left: r.left,
      width: r.width,
      maxHeight: maxH,
    });
  }, [portalList]);

  useLayoutEffect(() => {
    if (!open || !portalList) return;
    syncListPosition();
    const onScrollResize = () => syncListPosition();
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open, portalList, syncListPosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (listPortalRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected ? selected.label : (options[0]?.label ?? '—');

  const listClassName = `light-filter-select__list${portalList ? ' light-filter-select__list--portal' : ''}`;

  const listEl = open ? (
    <ul
      ref={portalList ? listPortalRef : undefined}
      className={listClassName}
      role="listbox"
      aria-label={ariaLabel}
      style={
        portalList
          ? {
              position: 'fixed',
              top: listBox.top,
              left: listBox.left,
              width: listBox.width,
              maxHeight: listBox.maxHeight,
              zIndex: 10050,
            }
          : undefined
      }
    >
      {options.map((opt) => (
        <li
          key={opt.value}
          role="option"
          aria-selected={opt.value === value}
          className={`light-filter-select__option ${opt.value === value ? 'light-filter-select__option--selected' : ''}`}
          onClick={() => {
            onChange(opt.value);
            setOpen(false);
          }}
        >
          {opt.label}
        </li>
      ))}
    </ul>
  ) : null;

  return (
    <div className={`light-filter-select ${className}`} ref={ref}>
      <button
        type="button"
        className="light-filter-select__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
      >
        <span className="light-filter-select__label">{displayLabel}</span>
        <span className="light-filter-select__chevron" aria-hidden="true">▼</span>
      </button>
      {!portalList && listEl}
      {portalList && listEl != null && createPortal(listEl, document.body)}
    </div>
  );
}
